package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initChronikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("chronik.md", FrontMatterElement.Builder { addScalar("title", "chronik"); addScalar("searchTitle", "Durable timer for Kotlin — scheduled inside your own transaction, at-least-once"); addScalar("description", "One primitive: schedule(id, at, payload), and at that instant the fact it is time leaves — never earlier, never lost. The row is written in the same transaction as your state change, which is the only reason to have it rather than cron."); addScalar("library", "chronik"); }))
}

@Page("/chronik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun ChronikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A state change that has to be undone in three days. A confirmation that must expire whether or not anybody comes back. A plan that ends on a date. Written the obvious way, each becomes a row with a timestamp and a job that scans for it — and the scan gets written again in every service, subtly differently, and none of them survives being run twice.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The awkward part is not the scanning. It is that the timer and the state change are ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("two writes")
        }
        org.jetbrains.compose.web.dom.Text(". If the state commits and the timer does not, nothing looks wrong: the operation succeeded, the row is correct, and only the thing nobody is watching for never happens.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The timer is written in your transaction.")
            }
            org.jetbrains.compose.web.dom.Text(" Not its own — yours. They commit together or neither does, which is the entire reason this exists rather than a cron job.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A lease, not a lock.")
            }
            org.jetbrains.compose.web.dom.Text(" A worker killed holding a timer loses it to another instance once the lease lapses, and no sooner. Several instances compete for rows through ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FOR UPDATE SKIP LOCKED")
            }
            org.jetbrains.compose.web.dom.Text(", with no leader and no coordinator to fall over.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Lateness is an outcome, not an error.")
            }
            org.jetbrains.compose.web.dom.Text(" A timer whose moment passed while the process was down fires on start-up, and how late it was is reported — the one number that tells a working worker from a stopped one.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Cancel and reschedule by id")
            }
            org.jetbrains.compose.web.dom.Text(", both first-class. Extending a deadline is one change to one row; expressed as cancel-then-schedule it has a window in which the timer does not exist.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("At-least-once, and it says so.")
            }
            org.jetbrains.compose.web.dom.Text(" Deduplication belongs to the receiver, on a key in the payload. Exactly-once is not promised anywhere, because it cannot be.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("It runs nothing.")
            }
            org.jetbrains.compose.web.dom.Text(" Only the fact \"it is time\" leaves; what to do with it is the receiver's decision. That refusal is what keeps it a primitive instead of somebody's job runner.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-deliberately-does-not-do") }) {
        org.jetbrains.compose.web.dom.Text("What it deliberately does not do")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Seconds, not milliseconds — a promise polling cannot keep is worse than no promise. No calendar and no time zones. No catching up: three periods down means one firing, not three, because for side-effecting work catching up is more dangerous than skipping.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("numbers") }) {
        org.jetbrains.compose.web.dom.Text("Numbers")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On one laptop with Postgres in a container beside it — a shape, not a throughput figure. With the index on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("(state, due_at)")
        }
        org.jetbrains.compose.web.dom.Text(" the claim does not grow between ten thousand and four hundred thousand waiting rows, holding at ~2 ms. Without it: 2 → 6 → 19 ms, which is a sequential scan drawn in milliseconds. Five thousand timers all due at once drain in under three seconds on one worker.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both figures came with a defect attached. The benchmark first measured connection setup rather than the query — 12 ms against a plan of 0.072 ms — and once that was fixed it showed that the claim was issuing one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("UPDATE")
        }
        org.jetbrains.compose.web.dom.Text(" per row, so the selection the method is named after was 0.3% of its own cost.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }

dependencies {
    implementation("io.github.youndie.chronik:chronik-core:0.1.0.11")
    implementation("io.github.youndie.chronik:chronik-postgres:0.1.0.11")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("chronik-postgres")
        }
        org.jetbrains.compose.web.dom.Text(" ships no driver, no connection pool and no DDL: it takes an Exposed ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Database")
        }
        org.jetbrains.compose.web.dom.Text(" and the table describes itself, indexes included, so a schema generator produces something that matches what the queries actually filter on.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("chronik-conformance")
        }
        org.jetbrains.compose.web.dom.Text(" is the corpus of cases every storage implementation has to satisfy — seventeen rules, each named by the rule rather than a number. It was written while there was one implementation on purpose: a corpus written after the second describes the intersection of the two, including whatever both get wrong.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Sagas wake from a fired timer through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-chronik")
        }
        org.jetbrains.compose.web.dom.Text(", in ")
        com.varabyte.kobweb.silk.components.navigation.Link("/petich") {
            org.jetbrains.compose.web.dom.Text("petich")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
